import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Container,
  Group,
  Skeleton,
  Switch,
  Table,
  Text,
  Title,
} from '@buildex/ui';
import { useUnit } from 'effector-react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { $tenants, $tenantsLoading, tenantActiveToggled } from '@/entities/tenant/model';
import { CreateTenantModal } from '@/features/tenant-crud/CreateTenantModal';
import { MODULE_LABELS } from '@/shared/module-meta';

export function TenantsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modalOpened, setModalOpened] = useState(false);
  const { tenants, loading, onToggleActive } = useUnit({
    tenants: $tenants,
    loading: $tenantsLoading,
    onToggleActive: tenantActiveToggled,
  });

  return (
    <Container fluid>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>{t('tenants.title')}</Title>
          <Text size="sm" c="dimmed">
            {t('tenants.subtitle')}
          </Text>
        </div>
        <Button onClick={() => setModalOpened(true)}>{t('tenants.create')}</Button>
      </Group>

      <Card p={0}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('tenants.name')}</Table.Th>
              <Table.Th>{t('tenants.slug')}</Table.Th>
              <Table.Th>{t('tenants.modules')}</Table.Th>
              <Table.Th>{t('tenants.users')}</Table.Th>
              <Table.Th w={140}>{t('tenants.status')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading
              ? Array.from({ length: 3 }, (_, i) => (
                  <Table.Tr key={i}>
                    {Array.from({ length: 5 }, (_, j) => (
                      <Table.Td key={j}>
                        <Skeleton height={14} />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))
              : tenants.map((tenant) => (
                  <Table.Tr
                    key={tenant.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tenants/${tenant.id}`)}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: tenant.branding.primaryColor,
                            display: 'inline-block',
                          }}
                        />
                        <Text size="sm" fw={600}>
                          {tenant.name}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {tenant.slug}.buildex.com
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {tenant.modules.length === 0 ? (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        ) : (
                          tenant.modules.map((m) => (
                            <Badge key={m} variant="light" title={MODULE_LABELS[m]}>
                              {m}
                            </Badge>
                          ))
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{tenant.users.length}</Text>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={tenant.active}
                        onChange={(e) =>
                          onToggleActive({ tenantId: tenant.id, active: e.currentTarget.checked })
                        }
                        label={tenant.active ? t('tenants.active') : t('tenants.blocked')}
                        size="sm"
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
          </Table.Tbody>
        </Table>
      </Card>

      <CreateTenantModal opened={modalOpened} onClose={() => setModalOpened(false)} />
    </Container>
  );
}
